"use client"

import { motion } from "@/components/motion/framer"

interface WireframeToPolishedProps {
  className?: string
}

export function WireframeToPolished({ className }: WireframeToPolishedProps) {
  // Check if this is a small container (like in service cards)
  const isSmall = className?.includes('h-32') || className?.includes('h-24') || className?.includes('h-20')
  // Check if this is a responsive container (hero sections)
  const isResponsive = className?.includes('w-full') && className?.includes('h-full')
  
  // For small containers, no scaling. For responsive containers, use responsive scaling.
  // For other cases (like homepage hero), use the original scaling.
  let scaleClass = ''
  if (isSmall) {
    scaleClass = ''
  } else if (isResponsive) {
    // Responsive scaling that works with container - scales based on viewport
    scaleClass = 'scale-[1.5] md:scale-[2] lg:scale-[2.5]'
  } else {
    // Original scaling for backward compatibility - reduced on mobile to prevent overflow
    scaleClass = 'scale-[1.2] sm:scale-[2.5]'
  }
  
  return (
    <div className={`relative h-full w-full ${className || ""}`}>
      <div className={`absolute inset-0 flex items-center justify-center gap-2 sm:gap-4 ${scaleClass}`}>
        {/* Wireframe structure - left side */}
        <motion.div
          className="relative h-24 w-20 rounded border-2 border-tk-slate/40 bg-transparent"
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {/* Website wireframe structure */}
          <div className="flex h-full w-full flex-col gap-1 p-1">
            {/* Header */}
            <div className="flex h-3 w-full items-center gap-1 border-b border-tk-slate/30 pb-0.5">
              <div className="h-1.5 w-1.5 rounded border border-tk-slate/40" />
              <div className="h-1.5 flex-1 rounded border border-tk-slate/40" />
              <div className="h-1.5 w-1.5 rounded border border-tk-slate/40" />
            </div>
            {/* Hero section */}
            <div className="h-4 w-full rounded border border-tk-slate/40">
              <div className="flex h-full w-full flex-col gap-0.5 p-0.5">
                <div className="h-1 w-3/4 rounded border border-tk-slate/30" />
                <div className="h-0.5 w-1/2 rounded border border-tk-slate/30" />
              </div>
            </div>
            {/* Content blocks */}
            <div className="flex h-3 w-full gap-1">
              <div className="h-full w-1/2 rounded border border-tk-slate/40">
                <div className="h-1 w-full border-b border-tk-slate/30" />
                <div className="h-0.5 w-2/3 border border-tk-slate/30" />
              </div>
              <div className="h-full w-1/2 rounded border border-tk-slate/40">
                <div className="h-1 w-full border-b border-tk-slate/30" />
                <div className="h-0.5 w-2/3 border border-tk-slate/30" />
              </div>
            </div>
            {/* Footer */}
            <div className="h-1.5 w-full rounded border border-tk-slate/40" />
          </div>
          {/* Arrow pointing right */}
          <motion.div
            className="absolute -right-3 top-1/2 -translate-y-1/2 text-tk-slate/40"
            animate={{ x: [0, 3, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            →
          </motion.div>
        </motion.div>

        {/* Polished design - right side */}
        <motion.div
          className="relative h-24 w-20 rounded-lg border-2 border-tk-teal/50 bg-gradient-to-br from-tk-linen via-tk-linen/95 to-tk-teal/5 shadow-md"
          animate={{ 
            opacity: [0.8, 1, 0.8],
            scale: [0.98, 1, 0.98]
          }}
          transition={{ duration: 2, delay: 0.3, repeat: Infinity }}
        >
          {/* Polished website design */}
          <div className="flex h-full w-full flex-col gap-1 p-1">
            {/* Header */}
            <div className="flex h-3 w-full items-center gap-1 border-b border-tk-slate/10 bg-tk-teal/10 px-1 pb-0.5">
              <div className="h-1.5 w-1.5 rounded-full bg-tk-teal/40" />
              <div className="h-1 flex-1 rounded bg-tk-slate/10" />
              <div className="h-1.5 w-1.5 rounded-full bg-tk-teal/40" />
            </div>
            {/* Hero section */}
            <div className="h-4 w-full rounded bg-gradient-to-br from-tk-teal/20 to-tk-teal/10">
              <div className="flex h-full w-full flex-col justify-center gap-0.5 p-0.5">
                <div className="h-1 w-3/4 rounded bg-tk-slate/20" />
                <div className="h-0.5 w-1/2 rounded bg-tk-slate/15" />
              </div>
            </div>
            {/* Content blocks */}
            <div className="flex h-3 w-full gap-1">
              <div className="h-full w-1/2 rounded bg-tk-teal/15">
                <div className="h-1 w-full bg-tk-teal/25" />
                <div className="h-0.5 w-2/3 rounded bg-tk-slate/10" />
              </div>
              <div className="h-full w-1/2 rounded bg-tk-teal/10">
                <div className="h-1 w-full bg-tk-teal/20" />
                <div className="h-0.5 w-2/3 rounded bg-tk-slate/10" />
              </div>
            </div>
            {/* Footer */}
            <div className="h-1.5 w-full rounded bg-tk-slate/10" />
          </div>
        </motion.div>
      </div>
    </div>
  )
}

